import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Dispatch, memo, SetStateAction, useCallback, useState } from 'react'
import { IoReorderThree, IoTrash } from 'react-icons/io5'
import { MdInfoOutline } from 'react-icons/md'

import { Droppable } from '@/components'
import type { QuestionOption } from '@/types'

interface OptionsListProps {
  options: QuestionOption[]
  setOptions: Dispatch<SetStateAction<QuestionOption[]>>
}

function OptionsList({ options, setOptions }: OptionsListProps) {
  const theme = useTheme()
  const [feedbackVisible, setFeedbackVisible] = useState<Record<number, boolean>>({})

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return

      const reordered = Array.from(options)
      const [moved] = reordered.splice(result.source.index, 1)
      reordered.splice(result.destination.index, 0, moved)

      const updatedOptions = reordered.map((option, index) => ({
        ...option,
        order: index + 1,
      }))

      setOptions(updatedOptions)
    },
    [options, setOptions],
  )

  const handleFeedback = useCallback((order: number) => {
    setFeedbackVisible((prevState) => ({
      ...prevState,
      [order]: !prevState[order],
    }))
  }, [])

  const handleDelete = useCallback(
    (index: number) => {
      setOptions((prev) =>
        prev
          .filter((_, i) => i !== index)
          .map((option, idx) => ({ ...option, order: idx + 1 })),
      )
    },
    [setOptions],
  )

  const getOptionLetter = useCallback(
    (order: number) => String.fromCharCode(96 + order),
    [],
  )

  if (options.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          border: `2px dashed ${theme.palette.divider}`,
          borderRadius: 2,
        }}>
        <Typography color="text.secondary">
          Nenhuma alternativa adicionada ainda
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Alternativas ({options.length})
        </Typography>
        <Chip
          label={
            options.some((opt) => opt.its_correct)
              ? 'Resposta definida'
              : 'Sem resposta correta'
          }
          color={options.some((opt) => opt.its_correct) ? 'success' : 'warning'}
          variant="outlined"
          size="small"
        />
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options">
          {(provided) => (
            <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={2}>
              {options.map((opt, index) => (
                <Draggable key={opt.id} draggableId={opt.id.toString()} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      elevation={snapshot.isDragging ? 8 : 2}
                      sx={{
                        borderRadius: 2,
                        border: `2px solid ${
                          opt.its_correct
                            ? theme.palette.success.light
                            : theme.palette.error.light
                        }`,
                        backgroundColor: opt.its_correct
                          ? theme.palette.success.light + '10'
                          : theme.palette.error.light + '10',
                        transition: theme.transitions.create(
                          ['box-shadow', 'transform'],
                          {
                            duration: theme.transitions.duration.short,
                          },
                        ),
                        '&:hover': {
                          boxShadow: theme.shadows[6],
                        },
                      }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              cursor: 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'text.secondary',
                              '&:active': { cursor: 'grabbing' },
                            }}>
                            <IoReorderThree size={20} />
                          </Box>

                          <Tooltip
                            title={`Alternativa ${opt.its_correct ? 'correta' : 'incorreta'}`}>
                            <Avatar
                              sx={{
                                backgroundColor: opt.its_correct
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                                color: 'white',
                                width: 32,
                                height: 32,
                                fontSize: '0.875rem',
                                fontWeight: 700,
                              }}>
                              {getOptionLetter(opt.order)}
                            </Avatar>
                          </Tooltip>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500, mb: 1 }}
                              dangerouslySetInnerHTML={{ __html: opt.option }}
                            />

                            {opt.feedback && (
                              <>
                                <Chip
                                  label={
                                    feedbackVisible[opt.order]
                                      ? 'Ocultar feedback'
                                      : 'Ver feedback'
                                  }
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  icon={<MdInfoOutline />}
                                  onClick={() => handleFeedback(opt.order)}
                                  sx={{ cursor: 'pointer' }}
                                />
                                <Collapse
                                  in={feedbackVisible[opt.order]}
                                  timeout="auto"
                                  unmountOnExit>
                                  <Box
                                    sx={{
                                      mt: 1,
                                      p: 2,
                                      borderRadius: 1,
                                      border: `1px solid ${theme.palette.info.light}`,
                                    }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      dangerouslySetInnerHTML={{ __html: opt.feedback }}
                                    />
                                  </Box>
                                </Collapse>
                              </>
                            )}
                          </Box>

                          <Tooltip title="Remover alternativa">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(index)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.palette.error.light + '20',
                                },
                              }}>
                              <IoTrash size={18} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  )
}

export default memo(OptionsList)
